import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { apiJson } from '@/lib/api';
import type { AdminProspectsListResponse, AdminProspectsSummaryResponse } from '@/lib/types';

const PAGE_SIZE = 50;

function pct(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${((100 * part) / whole).toFixed(1)}%`;
}

/** Avoid showing 0.0% when the true rate is small but non-zero (e.g. 5 / 50k list). */
function pctFromFraction(rate: number): string {
  const pct = 100 * rate;
  if (pct <= 0) return '0%';
  if (pct < 0.1) return `${pct.toFixed(3)}%`;
  if (pct < 1) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(1)}%`;
}

function AdminProspectsPage() {
  const { getToken } = useAuth();
  const [q, setQ] = useState('');
  const [tier, setTier] = useState<string>('');
  const [unsubFilter, setUnsubFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'id'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [likelySignedUpOnly, setLikelySignedUpOnly] = useState(false);

  const summaryQuery = useQuery({
    queryKey: ['admin', 'prospects', 'summary'],
    queryFn: () => apiJson<AdminProspectsSummaryResponse>(getToken, '/v1/admin/prospects/summary'),
  });

  const listParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(page * PAGE_SIZE));
    p.set('sort_by', sortBy);
    p.set('sort_dir', sortDir);
    if (q.trim()) p.set('q', q.trim());
    if (tier !== '') p.set('tier', tier);
    if (unsubFilter === 'yes') p.set('unsubscribed', 'true');
    if (unsubFilter === 'no') p.set('unsubscribed', 'false');
    if (likelySignedUpOnly) p.set('likely_signed_up', 'true');
    return p.toString();
  }, [page, q, tier, sortBy, sortDir, unsubFilter, likelySignedUpOnly]);

  const listQuery = useQuery({
    queryKey: ['admin', 'prospects', 'list', listParams],
    queryFn: () =>
      apiJson<AdminProspectsListResponse>(getToken, `/v1/admin/prospects?${listParams}`),
    placeholderData: keepPreviousData,
  });

  const s = summaryQuery.data;
  const list = listQuery.data;
  const totalPages = list ? Math.max(1, Math.ceil(list.total / PAGE_SIZE)) : 1;

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-semibold text-gray-900'>Prospects</h1>
        <p className='text-sm text-gray-500 mt-1'>
          Acquisition campaign performance and prospect list. Heuristic &quot;likely signed up&quot;
          matches prospect email to a non-deleted user row (shared inboxes can inflate).
        </p>
      </div>

      {summaryQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : summaryQuery.isError ? (
        <p className='text-red-600'>Could not load summary.</p>
      ) : !s ? null : (
        <>
          {!s.campaign_sending_enabled ? (
            <div className='rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-800'>
              <span className='font-semibold'>Prospect outbound emails off</span> —{' '}
              <span className='font-mono'>PROSPECT_ACQUISITION_CAMPAIGN_ENABLED</span> is false in{' '}
              <span className='font-mono'>campaigns/prospect_acquisition.py</span>. No sends run until
              that is set to true.
            </div>
          ) : s.campaign_paused ? (
            <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
              <span className='font-semibold'>Campaign paused</span> (deliverability guard or manual
              Redis flag). Sends may be stopped until cleared.
            </div>
          ) : (
            <div className='rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900'>
              <span className='font-semibold'>Campaign active</span> — not paused in Redis.
            </div>
          )}

          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2'>
            {(
              [
                ['Total prospects', s.total_prospects],
                ['With email', s.prospects_with_email],
                ['Active (email, not unsub)', s.prospects_active],
                ['Unsubscribed', s.prospects_unsubscribed],
                ['Unsub rate (of w/ email)', pctFromFraction(s.unsubscribed_rate_of_listable)],
                ['Likely signed up', s.likely_signed_up_count, true] as const,
                ['Parenting-plan leads', s.prospective_users_count],
                ['Total sends', s.total_sends],
                ['Sends (7d)', s.sends_last_7d],
                ['Sends (30d)', s.sends_last_30d],
                ['Opens (tracked)', s.total_opens],
                ['Open rate', `${(100 * s.open_rate_total).toFixed(1)}%`],
              ] as const
            ).map((entry) => {
              const [label, val, clickable] = entry.length === 3 ? entry : [...entry, false];
              const cardClass =
                'bg-white rounded-lg border border-gray-200 px-3 py-3 shadow-sm text-left w-full';
              if (clickable) {
                return (
                  <button
                    key={label}
                    type='button'
                    title='Show prospect rows whose email matches an active (non-deleted) user. Click again to clear.'
                    aria-pressed={likelySignedUpOnly}
                    onClick={() => {
                      setLikelySignedUpOnly((v) => !v);
                      setPage(0);
                    }}
                    className={`${cardClass} transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
                      likelySignedUpOnly ? 'ring-2 ring-primary ring-offset-1' : ''
                    }`}
                  >
                    <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight'>
                      {label}
                    </p>
                    <p className='mt-1 text-lg font-semibold tabular-nums text-gray-900'>{val}</p>
                  </button>
                );
              }
              return (
                <div key={label} className={cardClass}>
                  <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight'>
                    {label}
                  </p>
                  <p className='mt-1 text-lg font-semibold tabular-nums text-gray-900'>{val}</p>
                </div>
              );
            })}
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <div className='bg-white rounded-lg border border-gray-200 shadow-sm p-4'>
              <h2 className='text-sm font-semibold text-gray-900 mb-3'>Sequence funnel</h2>
              <p className='text-xs text-gray-500 mb-2'>Distinct prospects with at least one send</p>
              <ul className='divide-y divide-gray-100 text-sm'>
                {s.sequence.map((row) => (
                  <li key={row.email_key} className='py-2 flex justify-between gap-2'>
                    <span className='font-mono text-xs text-gray-700'>{row.email_key}</span>
                    <span className='tabular-nums font-semibold'>{row.prospects_reached}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className='bg-white rounded-lg border border-gray-200 shadow-sm p-4'>
              <h2 className='text-sm font-semibold text-gray-900 mb-3'>By tier</h2>
              <ul className='divide-y divide-gray-100 text-sm'>
                {s.tier_counts.map((row) => (
                  <li key={String(row.tier)} className='py-2 flex justify-between gap-2'>
                    <span className='text-gray-700'>
                      Tier {row.tier === null ? '—' : row.tier}
                    </span>
                    <span className='tabular-nums font-semibold'>{row.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <div className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
              <div className='px-4 py-3 border-b border-gray-100 bg-gray-50'>
                <h2 className='text-sm font-semibold text-gray-900'>Engagement by email</h2>
                <p className='text-xs text-gray-500 mt-0.5'>Opens = Mailgun first-open on send row</p>
              </div>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='px-4 py-2'>email_key</th>
                    <th className='px-4 py-2 text-right'>Sends</th>
                    <th className='px-4 py-2 text-right'>Opens</th>
                    <th className='px-4 py-2 text-right'>Open %</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {s.engagement_by_email_key.map((r) => (
                    <tr key={r.email_key} className='hover:bg-gray-50'>
                      <td className='px-4 py-2 font-mono text-xs'>{r.email_key}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{r.sends}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{r.opens}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{pct(r.opens, r.sends)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
              <div className='px-4 py-3 border-b border-gray-100 bg-gray-50'>
                <h2 className='text-sm font-semibold text-gray-900'>By variant (A/B)</h2>
                <p className='text-xs text-gray-500 mt-0.5'>From send rows; unknown = null variant</p>
              </div>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='px-4 py-2'>variant</th>
                    <th className='px-4 py-2 text-right'>Sends</th>
                    <th className='px-4 py-2 text-right'>Opens</th>
                    <th className='px-4 py-2 text-right'>Open %</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {[...s.variant_stats]
                    .sort((a, b) => b.sends - a.sends)
                    .map((r) => (
                      <tr key={r.variant} className='hover:bg-gray-50'>
                        <td className='px-4 py-2 font-mono text-xs'>{r.variant}</td>
                        <td className='px-4 py-2 text-right tabular-nums'>{r.sends}</td>
                        <td className='px-4 py-2 text-right tabular-nums'>{r.opens}</td>
                        <td className='px-4 py-2 text-right tabular-nums'>{pct(r.opens, r.sends)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className='text-xs text-gray-400'>
            Campaign <span className='font-mono'>{s.campaign_name}</span> · sequence keys:{' '}
            {s.email_sequence_keys.join(', ')}
          </p>
        </>
      )}

      <div className='border-t border-gray-200 pt-6 space-y-3'>
        <h2 className='text-lg font-semibold text-gray-900'>Prospect list</h2>
        {likelySignedUpOnly && (
          <div className='rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-gray-800'>
            <span className='font-semibold'>Likely signed up</span> filter is on — showing prospects
            whose email matches a non-deleted user account (same heuristic as the summary count).{' '}
            <button
              type='button'
              className='text-primary font-medium underline-offset-2 hover:underline'
              onClick={() => {
                setLikelySignedUpOnly(false);
                setPage(0);
              }}
            >
              Clear filter
            </button>
          </div>
        )}
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap gap-3 items-end'>
          <div className='flex-1 min-w-[200px]'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>Search email</label>
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
              placeholder='contains…'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <div>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>Tier</label>
            <select
              value={tier}
              onChange={(e) => {
                setTier(e.target.value);
                setPage(0);
              }}
              className='px-3 py-2 border border-gray-300 rounded-md text-sm'
            >
              <option value=''>All</option>
              <option value='1'>1</option>
              <option value='2'>2</option>
              <option value='3'>3</option>
            </select>
          </div>
          <div>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>Unsubscribed</label>
            <select
              value={unsubFilter}
              onChange={(e) => {
                setUnsubFilter(e.target.value as 'all' | 'yes' | 'no');
                setPage(0);
              }}
              className='px-3 py-2 border border-gray-300 rounded-md text-sm'
            >
              <option value='all'>All</option>
              <option value='yes'>Yes</option>
              <option value='no'>No</option>
            </select>
          </div>
          <div>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>Sort</label>
            <select
              value={`${sortBy}:${sortDir}`}
              onChange={(e) => {
                const [sb, sd] = e.target.value.split(':') as ['created_at' | 'id', 'asc' | 'desc'];
                setSortBy(sb);
                setSortDir(sd);
                setPage(0);
              }}
              className='px-3 py-2 border border-gray-300 rounded-md text-sm'
            >
              <option value='created_at:desc'>Created · newest</option>
              <option value='created_at:asc'>Created · oldest</option>
              <option value='id:desc'>ID · high</option>
              <option value='id:asc'>ID · low</option>
            </select>
          </div>
        </div>

        <div className='bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm'>
          <table className='w-full text-sm'>
            <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
              <tr>
                <th className='px-4 py-2'>ID</th>
                <th className='px-4 py-2'>Email</th>
                <th className='px-4 py-2'>Tier</th>
                <th className='px-4 py-2'>Variant</th>
                <th className='px-4 py-2'>Unsub</th>
                <th className='px-4 py-2 text-right'>Sends</th>
                <th className='px-4 py-2'>Last send</th>
                <th className='px-4 py-2'>Created</th>
                <th className='px-4 py-2'>Matched user</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {listQuery.isLoading ? (
                <tr>
                  <td colSpan={9} className='px-4 py-8 text-center text-gray-400'>
                    Loading…
                  </td>
                </tr>
              ) : listQuery.isError ? (
                <tr>
                  <td colSpan={9} className='px-4 py-8 text-center text-red-500'>
                    Failed to load list.
                  </td>
                </tr>
              ) : (
                (list?.rows ?? []).map((r) => (
                  <tr key={r.id} className='hover:bg-gray-50'>
                    <td className='px-4 py-2 font-mono text-xs'>{r.id}</td>
                    <td className='px-4 py-2 max-w-[220px] truncate' title={r.email ?? ''}>
                      {r.email ?? '—'}
                    </td>
                    <td className='px-4 py-2 tabular-nums'>{r.tier ?? '—'}</td>
                    <td className='px-4 py-2 font-mono text-xs'>{r.variant ?? '—'}</td>
                    <td className='px-4 py-2 text-xs'>
                      {r.unsubscribed_at ? (
                        <span className='text-amber-700 font-medium'>Yes</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className='px-4 py-2 text-right tabular-nums'>{r.campaign_sends_count}</td>
                    <td className='px-4 py-2 text-xs text-gray-600'>
                      {r.last_campaign_sent_at
                        ? new Date(r.last_campaign_sent_at).toLocaleString()
                        : '—'}
                    </td>
                    <td className='px-4 py-2 text-xs text-gray-600'>
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className='px-4 py-2 max-w-[200px]'>
                      {r.matched_user_id != null ? (
                        <Link
                          to='/admin/users/$userId'
                          params={{ userId: String(r.matched_user_id) }}
                          search={{ hid: undefined }}
                          title={`Open admin user #${r.matched_user_id}`}
                          className='text-primary font-medium text-sm hover:underline inline-block max-w-full truncate align-bottom'
                        >
                          {r.matched_user_label?.trim()
                            ? r.matched_user_label
                            : `User #${r.matched_user_id}`}
                        </Link>
                      ) : (
                        <span className='text-gray-400'>—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {list && list.total > 0 && (
          <div className='flex items-center justify-between text-sm text-gray-600'>
            <span>
              Showing {list.offset + 1}–{Math.min(list.offset + list.rows.length, list.total)} of{' '}
              {list.total}
            </span>
            <div className='flex gap-2'>
              <button
                type='button'
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
              >
                Prev
              </button>
              <span>
                Page {page + 1} / {totalPages}
              </span>
              <button
                type='button'
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
                className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/prospects')({
  component: AdminProspectsPage,
});
